import { Api } from '@/api/api.client';
import type { Components } from '@/api/generated/api';
import { WHO_AM_I_QUERY_KEY } from '@/api/query.keys';
import { useQueryClient } from '@tanstack/vue-query';

interface IAvatarPicturesComposable {
  getAvatarPictureUrl: (avatarPictureId: number | null) => string;
  uploadUserAvatarPicture: (file: File) => Promise<void>;
  uploadStudentAvatarPicture: (studentId: number, file: File) => Promise<Components.Schemas.StudentDto>;
}

/**
 * Single source of truth for user/student avatar pictures. Every upload mints a new file id on the
 * backend, so the served URL changes on each upload; invalidating the owning query is enough to
 * refresh the displayed picture immediately, no cache-busting query param needed.
 */
export function useAvatarPictures(): IAvatarPicturesComposable {
  const queryClient = useQueryClient();

  function getAvatarPictureUrl(avatarPictureId: number | null): string {
    return avatarPictureId == null ? '' : `/api/avatar-pictures/${avatarPictureId}`;
  }

  async function uploadUserAvatarPicture(file: File): Promise<void> {
    await Api.Routes.AvatarPicture.uploadMyAvatarPicture(null, { file });
    await queryClient.invalidateQueries({ queryKey: WHO_AM_I_QUERY_KEY });
  }

  async function uploadStudentAvatarPicture(studentId: number, file: File): Promise<Components.Schemas.StudentDto> {
    const response = await Api.Routes.AvatarPicture.uploadStudentAvatarPicture({ id: studentId }, { file });
    await queryClient.invalidateQueries({ queryKey: ['students'] });
    return response.data;
  }

  return {
    getAvatarPictureUrl,
    uploadUserAvatarPicture,
    uploadStudentAvatarPicture,
  };
}
